import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, MapPin, Star, Trash2, Users, Mail, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import {
  VendorSearchDto,
  StepVendorProps,
  CreateWorkOrderVendor,
  VendorContact,
  VendorCompliance,
} from '../../types';
import DataGrid from '../grid/DataGrid';
import ComplianceOverrideModal from '../modals/ComplianceOverrideModal';
import { getComplianceStatusClasses, isNonCompliant, requiresComplianceAcknowledgment } from '../../utils/vendorCompliance';
import { useSubscription } from '../../contexts/SubscriptionContext';

const StepVendor: React.FC<StepVendorProps> = ({ 
  data, 
  onChange, 
  onNext, 
  onPrevious,
  isSubmitting 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('distance');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [pendingVendor, setPendingVendor] = useState<VendorSearchDto | null>(null);
  const [complianceWarning, setComplianceWarning] = useState<VendorCompliance | null>(null);
  const [showComplianceOverrideModal, setShowComplianceOverrideModal] = useState(false);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');

  const {
    data: vendorsResponse,
    isLoading: vendorsLoading,
    error: vendorsError
  } = useQuery({
    queryKey: ['vendors', 'search', data.zipcode, data.radius, data.selectedCategories],
    queryFn: () => apiService.searchVendors(data.zipcode!, data.radius!, data.selectedCategories!),
    enabled: !!(data.zipcode && data.radius && data.selectedCategories && data.selectedCategories.length > 0),
    retry: 1,
    retryDelay: 1000
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100)
  });

  const {
    data: vendorContactsResponse,
    isLoading: vendorContactsLoading
  } = useQuery({
    queryKey: ['vendorPrimaryContacts', data.selectedVendors?.map(v => v.vendorId)],
    queryFn: async () => {
      if (!data.selectedVendors || data.selectedVendors.length === 0) return [];
      
      const contactPromises = data.selectedVendors.map(vendor => 
        apiService.getPrimaryVendorContact(vendor.vendorId)
      );
      
      const contactResponses = await Promise.all(contactPromises);
      return contactResponses.map(response => response.data);
    },
    enabled: !!(data.selectedVendors && data.selectedVendors.length > 0),
    retry: 1
  });

  const vendors = useMemo(() => vendorsResponse?.data || [], [vendorsResponse?.data]);
  const vendorContacts = vendorContactsResponse || [];

  const addVendorToSelection = useCallback((
    vendor: VendorSearchDto,
    complianceOverride?: { acknowledgeComplianceWarning: boolean; complianceOverrideReason: string }
  ) => {
    const currentVendors = data.selectedVendors || [];
    const newVendor: CreateWorkOrderVendor = {
      vendorId: vendor.id,
      isPrimary: currentVendors.length === 0,
      status: 'Assigned',
      notes: '',
      acknowledgeComplianceWarning: complianceOverride?.acknowledgeComplianceWarning,
      complianceOverrideReason: complianceOverride?.complianceOverrideReason,
    };
    onChange({ selectedVendors: [...currentVendors, newVendor] });
  }, [onChange, data.selectedVendors]);

  const handleVendorSelect = useCallback(async (vendor: VendorSearchDto) => {
    const currentVendors = data.selectedVendors || [];
    const existingVendor = currentVendors.find(v => v.vendorId === vendor.id);

    if (existingVendor) {
      const updatedVendors = currentVendors.filter(v => v.vendorId !== vendor.id);
      onChange({ selectedVendors: updatedVendors });
      return;
    }

    if (hasVendorCompliance && requiresComplianceAcknowledgment(vendor.complianceStatus, vendor.complianceStatusLabel)) {
      setPendingVendor(vendor);
      setIsLoadingCompliance(true);
      try {
        const response = await apiService.getVendorCompliance(vendor.id);
        if (response.success && response.data) {
          setComplianceWarning(response.data);
          setShowComplianceOverrideModal(true);
        } else {
          toast.error('Unable to verify vendor compliance. Assignment blocked until compliance is confirmed.');
        }
      } catch {
        toast.error('Unable to verify vendor compliance. Please try again.');
      } finally {
        setIsLoadingCompliance(false);
      }
      return;
    }

    addVendorToSelection(vendor);
  }, [addVendorToSelection, data.selectedVendors, hasVendorCompliance, onChange]);

  const handleComplianceOverrideConfirm = (reason: string) => {
    if (!pendingVendor) return;

    addVendorToSelection(pendingVendor, {
      acknowledgeComplianceWarning: true,
      complianceOverrideReason: reason,
    });
    setShowComplianceOverrideModal(false);
    setComplianceWarning(null);
    setPendingVendor(null);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSetPrimary = useCallback((vendorId: number) => {
    const currentVendors = data.selectedVendors || [];
    const updatedVendors = currentVendors.map(vendor => ({
      ...vendor,
      isPrimary: vendor.vendorId === vendorId
    }));
    onChange({ selectedVendors: updatedVendors });
  }, [onChange, data.selectedVendors]);

  const handleRemoveVendor = useCallback((vendorId: number) => {
    const currentVendors = data.selectedVendors || [];
    const updatedVendors = currentVendors.filter(v => v.vendorId !== vendorId);
    if (updatedVendors.length > 0 && !updatedVendors.some(v => v.isPrimary)) {
      updatedVendors[0].isPrimary = true;
    }
    onChange({ selectedVendors: updatedVendors });
  }, [onChange, data.selectedVendors]);

  const handleNext = () => {
    if (hasVendorCompliance) {
      const vendorsMissingOverride = (data.selectedVendors || []).filter((selectedVendor) => {
        const vendor = vendors.find(v => v.id === selectedVendor.vendorId);
        if (!vendor) return false;
        return requiresComplianceAcknowledgment(vendor.complianceStatus, vendor.complianceStatusLabel)
          && !selectedVendor.acknowledgeComplianceWarning;
      });

      if (vendorsMissingOverride.length > 0) {
        toast.error('One or more selected vendors require a compliance override before continuing.');
        return;
      }
    }

    onNext();
  };

  const columns = useMemo(() => {
    const allColumns = [
    {
      id: 'selection',
      header: '',
      sortable: false,
      cell: (_value: unknown, row: VendorSearchDto) => {
        const currentVendors = data.selectedVendors || [];
        const isSelected = currentVendors.some(v => v.vendorId === row.id);
        const needsOverride = requiresComplianceAcknowledgment(row.complianceStatus, row.complianceStatusLabel);
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleVendorSelect(row)}
              disabled={isLoadingCompliance}
              title={needsOverride ? 'Compliance override required to assign this vendor' : undefined}
              className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
          </div>
        );
      }
    },
    {
      id: 'complianceStatusLabel',
      header: 'Compliance',
      sortable: false,
      cell: (_value: string, row: VendorSearchDto) => (
        row.complianceStatusLabel ? (
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(row.complianceStatus, row.complianceStatusLabel)}`}>
            {row.complianceStatusLabel}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      )
    },
    {
      id: 'isActive',
      header: 'Active',
      sortable: true,
      cell: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'categories',
      header: 'Categories',
      sortable: false,
      cell: (_value: string[], row: VendorSearchDto) => (
        <div className="flex flex-wrap gap-1">
          {row.categories?.slice(0, 2).map((category: string, index: number) => (
            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-color-100 text-accent-color-800">
              {category}
            </span>
          ))}
          {row.categories && row.categories.length > 2 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
              +{row.categories.length - 2}
            </span>
          )}
        </div>
      )
    },
    {
      id: 'distance',
      header: 'Distance',
      sortable: true,
      cell: (value: number) => (
        <div className="flex items-center text-gray-600 dark:text-gray-400">
          <MapPin className="h-4 w-4 mr-1" />
          {value ? value.toFixed(1) : '-'} miles
        </div>
      )
    },
    {
      id: 'name',
      header: 'Vendor Name',
      sortable: true,
      cell: (value: string) => (
        <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
      )
    },
    {
      id: 'rating',
      header: 'Rating',
      sortable: true,
      cell: (value: number) => (
        <div className="flex items-center">
          <Star className="h-4 w-4 text-yellow-400 fill-current" />
          <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">{value ? value.toFixed(1) : '-'}</span>
        </div>
      )
    },
    {
      id: 'rank',
      header: 'Rank',
      sortable: true,
      cell: (value: number) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    {
      id: 'city',
      header: 'City',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    {
      id: 'state',
      header: 'State',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    {
      id: 'zipCode',
      header: 'Zip',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    }
    ];

    return hasVendorCompliance
      ? allColumns
      : allColumns.filter((column) => column.id !== 'complianceStatusLabel');
  }, [data.selectedVendors, handleVendorSelect, hasVendorCompliance, isLoadingCompliance]);

  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendors;
    return vendors.filter(vendor => 
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.state?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [vendors, searchTerm]);

  const pagedVendors = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredVendors.slice(startIndex, startIndex + pageSize);
  }, [filteredVendors, currentPage, pageSize]);

  if (vendorsError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Error loading vendors</div>
        <div className="text-gray-500 mt-2">Please try again</div>
      </div>
    );
  }

  if (!data.zipcode || !data.radius || !data.selectedCategories || data.selectedCategories.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Missing required data from Step 1</div>
        <div className="text-gray-500 mt-2">Please go back to Step 1 and complete the client selection</div>
        <button
          type="button"
          onClick={onPrevious}
          className="mt-4 btn-secondary"
        >
          Back to Step 1
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-700 rounded-md p-3">
        <h4 className="text-sm font-medium text-accent-color-700 dark:text-accent-color-300 mb-2">Search Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center">
            <span className="text-accent-color dark:text-accent-color-400 font-medium">Zipcode:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">{data.zipcode}</span>
          </div>
          <div className="flex items-center">
            <span className="text-accent-color dark:text-accent-color-400 font-medium">Radius:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">{data.radius} miles</span>
          </div>
          <div>
            <div className="flex items-center">
              <span className="text-accent-color dark:text-accent-color-400 font-medium">Categories:</span>
              <span className="ml-2 text-accent-color-800 dark:text-accent-color-200 text-xs">{data.selectedCategories?.length || 0} selected</span>
            </div>
            {data.selectedCategories && data.selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {data.selectedCategories.map((categoryId, index) => {
                  const category = categoriesResponse?.data?.data?.find(c => c.id === categoryId);
                  return (
                    <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-accent-color-100 dark:bg-accent-color-800 text-accent-color-800 dark:text-accent-color-200">
                      {category ? category.name : `Category-${categoryId}`}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {data.selectedVendors && data.selectedVendors.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md p-4">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-3">Selected Vendors ({data.selectedVendors.length})</h4>
          <div className="space-y-3">
            {data.selectedVendors.map((selectedVendor) => {
              const vendor = vendors.find(v => v.id === selectedVendor.vendorId);
              const primaryContact = vendorContacts.find(contact => contact?.vendorId === selectedVendor.vendorId);
              if (!vendor) return null;

              const vendorIsNonCompliant = hasVendorCompliance && isNonCompliant(vendor.complianceStatus, vendor.complianceStatusLabel);
              const missingOverride = hasVendorCompliance && requiresComplianceAcknowledgment(vendor.complianceStatus, vendor.complianceStatusLabel)
                && !selectedVendor.acknowledgeComplianceWarning;
              
              return (
                <div
                  key={selectedVendor.vendorId}
                  className={`bg-white dark:bg-gray-800 border rounded-md p-3 ${
                    missingOverride
                      ? 'border-red-400 dark:border-red-600 ring-1 ring-red-300 dark:ring-red-700'
                      : vendorIsNonCompliant
                        ? 'border-red-200 dark:border-red-700'
                        : 'border-green-200 dark:border-green-600'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{vendor.name}</span>
                        {selectedVendor.isPrimary && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-color-100 dark:bg-accent-color-800 text-accent-color-800 dark:text-accent-color-200">
                            Primary
                          </span>
                        )}
                        {hasVendorCompliance && vendor.complianceStatusLabel && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(vendor.complianceStatus, vendor.complianceStatusLabel)}`}>
                            {vendor.complianceStatusLabel}
                          </span>
                        )}
                        {missingOverride && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-600 text-white">
                            Override required
                          </span>
                        )}
                        {selectedVendor.complianceOverrideReason && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            Override logged
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div>
                          <span className="font-medium">Distance:</span>
                          <span className="ml-1">{vendor.distance ? vendor.distance.toFixed(1) : '-'} miles</span>
                        </div>
                        <div>
                          <span className="font-medium">Location:</span>
                          <span className="ml-1">{vendor.city}, {vendor.state} {vendor.zipCode}</span>
                        </div>
                        <div>
                          <span className="font-medium">Rating:</span>
                          <span className="ml-1">{vendor.rating ? `${vendor.rating.toFixed(1)}/5` : 'Not rated'}</span>
                        </div>
                      </div>
                      {selectedVendor.complianceOverrideReason && (
                        <p className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
                          Override reason: {selectedVendor.complianceOverrideReason}
                        </p>
                      )}
                      {primaryContact && (
                        <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-600">
                          <div className="flex items-center space-x-1 text-xs text-green-700 dark:text-green-300">
                            <Users className="h-3 w-3" />
                            <span className="font-medium">Primary Contact:</span>
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                            <div className="font-medium">{primaryContact.fullName}</div>
                            {primaryContact.title && (
                              <div>{primaryContact.title}</div>
                            )}
                            {primaryContact.email && (
                              <div className="flex items-center space-x-1 mt-1">
                                <Mail className="h-3 w-3" />
                                <a href={`mailto:${primaryContact.email}`} className="hover:underline text-green-600 dark:text-green-400">
                                  {primaryContact.email}
                                </a>
                              </div>
                            )}
                            {primaryContact.phone && (
                              <div className="flex items-center space-x-1 mt-1">
                                <Phone className="h-3 w-3" />
                                <span>{primaryContact.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {!selectedVendor.isPrimary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimary(selectedVendor.vendorId)}
                          className="text-xs bg-accent-color-100 dark:bg-accent-color-800 text-accent-color-700 dark:text-accent-color-200 px-2 py-1 rounded hover:bg-accent-color-200 dark:hover:bg-accent-color-600 transition-colors"
                        >
                          Set Primary
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveVendor(selectedVendor.vendorId)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Select Vendor</h3>
        {vendorsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-color mx-auto"></div>
            <div className="text-gray-500 mt-2">Searching for vendors...</div>
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-600 dark:text-gray-400 text-lg font-medium">No vendors found</div>
            <div className="text-gray-500 dark:text-gray-500 mt-2">
              No vendors found within {data.radius} miles of {data.zipcode} with the selected categories.
            </div>
            <button
              type="button"
              onClick={onPrevious}
              className="mt-4 btn-secondary"
            >
              Back to Step 1
            </button>
          </div>
        ) : (
          <DataGrid
            data={pagedVendors}
            columns={columns}
            isLoading={false}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={filteredVendors.length}
            totalPages={Math.ceil(filteredVendors.length / pageSize)}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
            searchPlaceholder="Search vendors..."
            emptyMessage="No vendors found"
          />
        )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</h4>
        <div className="space-y-1 text-sm">
          <div className={`flex items-center ${(data.selectedVendors?.length || 0) > 0 ? 'text-green-600' : 'text-accent-color'}`}>
            <Check className={`h-4 w-4 mr-2 ${(data.selectedVendors?.length || 0) > 0 ? 'text-green-600' : 'text-accent-color'}`} />
            {(data.selectedVendors?.length || 0) > 0 ? `${data.selectedVendors.length} vendor${data.selectedVendors.length !== 1 ? 's' : ''} selected` : 'No vendors selected (optional)'}
          </div>
          <div className="text-green-600 flex items-center">
            <Check className="h-4 w-4 mr-2 text-green-600" />
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} found within {data.radius} miles
          </div>
          {(data.selectedVendors?.length || 0) > 0 && (
            <div className="text-accent-color flex items-center">
              <Check className="h-4 w-4 mr-2 text-accent-color" />
              Primary vendor: {vendors.find(v => data.selectedVendors?.find(sv => sv.vendorId === v.id && sv.isPrimary))?.name || 'Not set'}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="btn-secondary"
        >
          Back to Step 1
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting || isLoadingCompliance}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next Step
        </button>
      </div>

      {hasVendorCompliance && (
        <ComplianceOverrideModal
          isOpen={showComplianceOverrideModal}
          onClose={() => {
            setShowComplianceOverrideModal(false);
            setComplianceWarning(null);
            setPendingVendor(null);
          }}
          compliance={complianceWarning}
          onConfirm={handleComplianceOverrideConfirm}
          isLoading={isLoadingCompliance}
        />
      )}
    </div>
  );
};

export default StepVendor;
